import React, { useState } from "react";
import { useInfiniteQuery, useQuery } from "react-query";
import { useOrganizationQuery } from "../../hooks/useOrganizationQuery";
import ApiService from "../../services/ApiService";
import {
  Banner,
  Button,
  SelectOption,
  Spinner,
  Table,
} from "@cinesys/ui/components";
import styled from "styled-components";
import { useScriptExecutionValues } from "./jobUtil";
import JobView from "./JobView";
import { RefreshCw } from "react-feather";

interface Props {
  systemDomainId?: string;
  instanceId?: string;
  showOrganization?: boolean;
  modal?: boolean;
  dlq?: boolean;
  error?: boolean;
  warning?: boolean;
}

const RefreshWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 2px 0;
`;

const StyledRow = styled.div<{ $columns: string }>`
  display: grid;
  grid-template-columns: ${(props) => props.$columns};
  column-gap: 10px;
  align-items: center;

  > span {
    display: flex;
    align-items: center;

    > * + * {
      margin-left: 5px;
    }
  }
`;

const Count = styled.div`
  color: var(--gray100);
  padding: 5px 10px;
  font-size: 14px;
`;

const JobRow = (props: {
  scriptExecution: ScriptExecutionStore;
  organizations?: SelectOption[];
  showOrganization?: boolean;

  columns: { width: string }[];
}) => {
  const { scriptExecution, organizations, columns, showOrganization } = props;
  const values = useScriptExecutionValues(scriptExecution, organizations);
  return (
    <StyledRow $columns={columns.map(({ width }) => width).join(" ")}>
      {showOrganization && <span>{values?.systemDomainId.value}</span>}
      <span>
        {/* {scriptExecution.development && <Badge color="var(--orange100)" label="Dev" />} */}
        <span>{values?.title.value}</span>
      </span>
      <span>{values?.status.value}</span>
      <span>{values?.progress.value}</span>
      <span>{values?.receivedDate.value}</span>
    </StyledRow>
  );
};

const JobList = (props: Props) => {
  const {
    systemDomainId,
    instanceId,
    showOrganization,
    modal,
    dlq,
    error,
    warning,
  } = props;
  const [current, setCurrent] = useState<ScriptExecutionStore | undefined>();

  const { data: errorCountData } = useQuery({
    queryKey: ["jobList", "errorCount"],
    queryFn: async () => {
      const response = await ApiService.fetch<{ count: number }>(
        "GET",
        "/api/script_execution/error/count"
      );
      return response;
    },
    enabled: !!error && !systemDomainId,
  });

  const { data: warningCountData } = useQuery({
    queryKey: ["jobList", "warningCount"],
    queryFn: async () => {
      const response = await ApiService.fetch<{ count: number }>(
        "GET",
        "/api/script_execution/warning/count"
      );
      return response;
    },
    enabled: !!warning && !systemDomainId,
  });

  const {
    data,
    isLoading,
    isError,
    refetch,
    isFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: [
      "jobList",
      `${systemDomainId}_${instanceId}`,
      dlq,
      error,
      warning,
    ],
    queryFn: async ({ pageParam }) => {
      if (systemDomainId && instanceId) {
        const url = pageParam
          ? `/api/script_execution/${systemDomainId}/${instanceId}?lastEvaluatedKey=${encodeURIComponent(
              pageParam
            )}`
          : `/api/script_execution/${systemDomainId}/${instanceId}`;

        const response = await ApiService.fetch<{
          data: ScriptExecutionStore[];
          count?: number;
          lastEvaluatedKey: string;
        }>("GET", url);
        return response;
      } else {
        const globalPath = dlq
          ? "/dlq"
          : error
          ? "/error"
          : warning
          ? "/warning"
          : "";
        const url = pageParam
          ? `/api/script_execution${globalPath}?lastEvaluatedKey=${encodeURIComponent(
              pageParam
            )}`
          : `/api/script_execution${globalPath}`;

        const response = await ApiService.fetch<{
          data: ScriptExecutionStore[];
          count?: number;
          lastEvaluatedKey: string;
        }>("GET", url);
        return response;
      }
    },
    getNextPageParam: (lastPage) => {
      return lastPage.lastEvaluatedKey || undefined;
    },
  });

  const columns = [
    {
      title: "Job Title",
      width: "1fr",
    },
    {
      title: "Status",
      width: "80px",
    },
    {
      title: "Progress",
      width: "0.75fr",
    },
    {
      title: (
        <RefreshWrapper>
          <span>Received</span>
          <Button
            icon={RefreshCw}
            loading={isFetching}
            onClick={() => {
              refetch();
            }}
          />
        </RefreshWrapper>
      ),
      width: "160px",
    },
  ];

  if (showOrganization) {
    columns.unshift({
      title: "Organization",
      width: "1fr",
    });
  }

  const { data: organizations } = useOrganizationQuery(showOrganization);

  return isLoading ? (
    <Spinner />
  ) : isError ? (
    <Banner error="Error loading jobs" />
  ) : (
    <>
      {error && errorCountData?.count !== undefined && (
        <Count>{errorCountData.count} errors</Count>
      )}
      {warning && warningCountData?.count !== undefined && (
        <Count>{warningCountData.count} warnings</Count>
      )}
      {!error && !warning && data?.pages[0].count !== undefined && (
        <Count>{data?.pages[0].count} jobs</Count>
      )}
      <Table
        style={
          {
            "--tableHeaderBg": "transparent",
            "--tableHeaderBorderBottom": "1px solid var(--dark300)",
            "--tableHeaderShadow": "none",
          } as React.CSSProperties
        }
        columns={columns}
        rows={[
          ...(data?.pages
            .flatMap((page) => page.data)
            .map((scriptExecution) => ({
              id: scriptExecution.id,
              link: !modal
                ? `${scriptExecution.systemDomainId}/${scriptExecution.instanceId}/${scriptExecution.id}`
                : undefined,
              onClick: modal ? () => setCurrent(scriptExecution) : undefined,
              element: () => (
                <JobRow
                  scriptExecution={scriptExecution}
                  organizations={organizations}
                  showOrganization={showOrganization}
                  columns={columns}
                />
              ),
            })) ?? []),
          ...(hasNextPage
            ? [
                {
                  id: "nextPage",
                  element: () =>
                    isFetchingNextPage ? (
                      <Spinner />
                    ) : (
                      <Button
                        label="Load more"
                        onClick={() => fetchNextPage()}
                        type="default"
                        borderless
                      />
                    ),
                },
              ]
            : []),
        ]}
        autoHeight={{ twoColumn: true }}
      />
      {current && (
        <JobView
          modal
          id={current.id}
          systemDomainId={systemDomainId}
          instanceId={current.instanceId}
          onClose={() => setCurrent(undefined)}
        />
      )}
    </>
  );
};

export default JobList;
